import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Search, Sparkles, MapPin, Zap, Award, Users, Radio, Crown, ArrowRight } from "lucide-react";
import { flags } from "@/config/featureFlags";
import useMotionPreferences from "@/hooks/useMotionPreferences";
import AwardBadge3D from "./AwardBadge3D";

const ROTATING_WORDS = ["local business", "local pro", "local service", "local directory"];

/**
 * Homepage3DHero — premium animated LocalBest city/map hero.
 *
 * Composition (no WebGL, no new deps):
 *   - Layered SVG city skyline + connection lines
 *   - Floating listing cards (3 samples), parallax-drifting
 *   - Glowing Best of Best badges
 *   - Headline + stats + CTA layered above (search lives in the section below)
 *
 * Fallbacks:
 *   - Flag off: renders nothing (caller should render their old hero).
 *   - Mobile: single-column, simplified card carousel, no float animation.
 *   - Reduced motion: static gradient + search bar only.
 *
 * Props:
 *   - featuredListings: [{ name, category_name, city, state, is_best_of_best, logo_url }]
 *   - stats: { total_businesses, best_of_best, categories }
 */
export default function Homepage3DHero({ featuredListings = [], stats = {} }) {
  const enabled = flags.ENABLE_3D_HOMEPAGE_HERO;
  const { prefersReducedMotion, isMobile } = useMotionPreferences();
  const [wordIdx, setWordIdx] = useState(0);

  // Rotate the hero word every 2.4s — pauses when the user prefers reduced motion.
  useEffect(() => {
    if (prefersReducedMotion || !enabled) return;
    const t = setInterval(() => setWordIdx((i) => (i + 1) % ROTATING_WORDS.length), 2400);
    return () => clearInterval(t);
  }, [prefersReducedMotion, enabled]);

  const scrollToSearch = () => {
    const el = document.querySelector('[data-testid="directory-search-input"]');
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => el.focus({ preventScroll: true }), 650);
  };

  const animate = enabled && !prefersReducedMotion;

  if (!enabled) return null;

  return (
    <section
      className="relative overflow-hidden"
      style={{
        minHeight: isMobile ? 400 : 500,
        background:
          "radial-gradient(ellipse at 15% 20%, rgba(124,58,237,0.30) 0%, transparent 55%), radial-gradient(ellipse at 85% 80%, rgba(0,229,255,0.22) 0%, transparent 55%), radial-gradient(ellipse at 75% 20%, rgba(245,158,11,0.16) 0%, transparent 45%), linear-gradient(180deg, #0B1220 0%, #050915 100%)",
      }}
      data-testid="homepage-3d-hero"
    >
      {/* -------- Background city grid + connection lines (SVG) -------- */}
      <svg
        aria-hidden
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 1440 720"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <linearGradient id="lb-hero-grid" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(124,58,237,0.20)" />
            <stop offset="100%" stopColor="rgba(0,229,255,0.05)" />
          </linearGradient>
          <linearGradient id="lb-hero-line" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(0,229,255,0)" />
            <stop offset="50%" stopColor="rgba(0,229,255,0.7)" />
            <stop offset="100%" stopColor="rgba(0,229,255,0)" />
          </linearGradient>
        </defs>

        {/* Perspective grid — city-map feel */}
        {Array.from({ length: 24 }).map((_, i) => (
          <line
            key={`h-${i}`}
            x1={0}
            y1={360 + i * 15}
            x2={1440}
            y2={360 + i * 15}
            stroke="url(#lb-hero-grid)"
            strokeWidth={i === 0 ? 1.4 : 0.5}
            opacity={0.3}
          />
        ))}
        {Array.from({ length: 18 }).map((_, i) => {
          const x = i * 80;
          return (
            <line
              key={`v-${i}`}
              x1={x}
              y1={360}
              x2={720 + (x - 720) * 3}
              y2={720}
              stroke="url(#lb-hero-grid)"
              strokeWidth={0.4}
              opacity={0.25}
            />
          );
        })}

        {/* Glowing connection line between nodes */}
        {animate && !isMobile && (
          <motion.path
            d="M 200 200 Q 550 100 900 220 T 1300 320"
            stroke="url(#lb-hero-line)"
            strokeWidth={1.6}
            fill="none"
            initial={{ pathLength: 0, opacity: 0.4 }}
            animate={{ pathLength: 1, opacity: [0.4, 0.9, 0.4] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          />
        )}

        {/* Sparse "city" dots along the horizon */}
        {[...Array(28)].map((_, i) => {
          const x = 40 + i * 51;
          const y = 355 + Math.sin(i) * 5;
          return (
            <circle
              key={`c-${i}`}
              cx={x}
              cy={y}
              r={1.5}
              fill="#00E5FF"
              opacity={0.55}
            />
          );
        })}
      </svg>

      {/* -------- Floating showcase card (desktop only) — generic Best of Best template so it represents ANY listing type -------- */}
      {!isMobile && (
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <motion.div
            className="absolute w-[260px] pointer-events-auto"
            style={{ top: "22%", left: "6%" }}
            initial={{ opacity: 0, y: 30, rotate: -6 }}
            animate={{ opacity: 1, y: 0, rotate: -6 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            data-testid="homepage-3d-hero-showcase-card"
          >
            <motion.div
              className="rounded-2xl backdrop-blur-md p-4 shadow-2xl border"
              style={{
                background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(11,18,32,0.9))",
                borderColor: "rgba(245,158,11,0.55)",
                boxShadow:
                  "0 20px 55px -12px rgba(245,158,11,0.45), inset 0 1px 0 rgba(255,255,255,0.08)",
              }}
              animate={animate ? { y: [0, -8, 0], rotate: [-6, -5, -6] } : undefined}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-[#FBBF24] mb-1">
                    Best of Best
                  </p>
                  <p className="text-white text-sm font-bold leading-tight">
                    AI Horse-Powered Listing
                  </p>
                </div>
                <AwardBadge3D
                  variant="gold"
                  size="md"
                  testid="homepage-3d-hero-showcase-badge"
                />
              </div>
              <div className="flex items-center gap-1.5 flex-wrap mb-3">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/30">
                  AI Chat Active
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#7C3AED]/15 text-[#A78BFA] border border-[#7C3AED]/30">
                  AI Voice
                </span>
              </div>
              <div className="flex items-center gap-2 text-[11px] text-[#94A3B8]">
                <MapPin className="w-3 h-3" />
                Your City · Your Category
              </div>
            </motion.div>
          </motion.div>
        </div>
      )}

      {/* -------- Center content — headline + stats + CTAs -------- */}
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/30 text-[#FBBF24] text-xs font-semibold mb-4"
          data-testid="homepage-3d-hero-tag"
        >
          <Sparkles className="w-3.5 h-3.5" />
          The AI-Powered Best of Best Marketplace
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Outfit'] text-white leading-[1.05] mb-4"
          data-testid="homepage-3d-hero-title"
        >
          Find the{" "}
          <span
            className="bg-clip-text text-transparent"
            style={{
              backgroundImage: "linear-gradient(135deg, #F59E0B, #FBBF24 40%, #FDE68A 70%, #F59E0B)",
              WebkitBackgroundClip: "text",
            }}
          >
            Best of Best
          </span>
          <br className="hidden sm:block" />{" "}
          <span
            className="relative inline-block align-baseline"
            style={{ minHeight: "1.1em", minWidth: prefersReducedMotion ? undefined : "9ch" }}
            aria-live="polite"
          >
            {prefersReducedMotion ? (
              <span>local pro</span>
            ) : (
              <AnimatePresence mode="wait">
                <motion.span
                  key={ROTATING_WORDS[wordIdx]}
                  initial={{ opacity: 0, y: 14, filter: "blur(4px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0)" }}
                  exit={{ opacity: 0, y: -14, filter: "blur(4px)" }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                  className="inline-block bg-clip-text text-transparent"
                  style={{
                    backgroundImage: "linear-gradient(135deg, #7C3AED, #00E5FF 60%, #10B981)",
                    WebkitBackgroundClip: "text",
                  }}
                  data-testid={`homepage-3d-hero-rotating-word-${ROTATING_WORDS[wordIdx].replace(/\s+/g, "-")}`}
                >
                  {ROTATING_WORDS[wordIdx]}
                </motion.span>
              </AnimatePresence>
            )}
          </span>{" "}
          — powered by AI.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="text-sm sm:text-base text-[#94A3B8] max-w-xl mx-auto mb-8"
        >
          One search away from a vetted, award-winning local business with an AI Hero standing by 24/7.
        </motion.p>

        {/* Stat pills */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.55 }}
          className="flex flex-wrap items-center justify-center gap-3"
        >
          {typeof stats.total_businesses === "number" && stats.total_businesses > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/85 text-xs">
              <Users className="w-3.5 h-3.5" /> {stats.total_businesses.toLocaleString()}+ verified pros
            </span>
          )}
          {typeof stats.best_of_best === "number" && stats.best_of_best > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#FBBF24] text-xs">
              <Award className="w-3.5 h-3.5" /> {stats.best_of_best.toLocaleString()} Best of Best
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/85 text-xs">
            <Zap className="w-3.5 h-3.5" /> 1000's of categories
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30 text-[#00E5FF] text-xs">
            <Radio className="w-3.5 h-3.5" /> AI Chat + Voice Live
          </span>
        </motion.div>

        {/* Primary CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-8 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 sm:gap-4"
        >
          <motion.button
            type="button"
            onClick={scrollToSearch}
            whileHover={{ scale: 1.04, y: -2 }}
            whileTap={{ scale: 0.97 }}
            className="group inline-flex items-center justify-center gap-2.5 w-full sm:w-auto px-7 py-3.5 rounded-full text-sm sm:text-base font-bold text-[#0A0F1C] shadow-[0_10px_35px_-8px_rgba(245,158,11,0.6)]"
            style={{ background: "linear-gradient(135deg, #F59E0B, #FBBF24 55%, #F59E0B)" }}
            data-testid="homepage-3d-hero-cta-search"
          >
            <Search className="w-5 h-5" />
            Search for Best of Best
            <ArrowRight className="w-4 h-4 opacity-70 group-hover:translate-x-1 transition-transform" />
          </motion.button>

          <motion.div whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.97 }} className="w-full sm:w-auto">
            <Link
              to="/?create=true"
              className="inline-flex items-center justify-center gap-2.5 w-full px-7 py-3.5 rounded-full text-sm sm:text-base font-bold text-white shadow-[0_10px_35px_-8px_rgba(124,58,237,0.65)]"
              style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7 55%, #6D28D9)" }}
              data-testid="homepage-3d-hero-cta-free-listing"
            >
              <Sparkles className="w-5 h-5" />
              Get Free Listing
            </Link>
          </motion.div>

          <motion.div whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.97 }} className="w-full sm:w-auto">
            <Link
              to="/onlybest"
              className="inline-flex items-center justify-center gap-2.5 w-full px-7 py-3.5 rounded-full text-sm sm:text-base font-bold text-white bg-white/5 backdrop-blur-md border border-[#DC2626]/50 hover:border-[#2563EB]/60 hover:bg-white/10 shadow-[0_10px_35px_-10px_rgba(220,38,38,0.45)] transition-colors"
              data-testid="homepage-3d-hero-cta-onlybest"
            >
              <Crown className="w-5 h-5 text-[#DC2626]" />
              <span className="text-center leading-tight">Become an OnlyBest Creator or Affiliate Marketer</span>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
